import CONFIG from "../config/config";
import { apiFetch } from "./http";

/**
 * search.api.js — client for the customer-portal global search surface.
 *
 * Backend contract (controller/v1/searchMine.v1.controller.js):
 *   GET /api/v1/search/mine?q=&types=&limit=  -> { query, groups: [{ type, total, items }], totalResults }
 *   GET /api/v1/search/mine/types             -> { types: string[] }
 *
 * This is the PORTAL endpoint, not the admin one: every group is filtered to what
 * the logged-in user was granted before it is counted or returned, so the palette
 * can render whatever comes back without doing any access checks of its own.
 *
 * An `item` is normalized server-side and always carries:
 *   { id, type, title, subtitle, route, meta? }
 * so the palette renders one row component for all five entity types and never
 * assembles a route itself (the server resolves lineage — e.g. which page can
 * actually open a venue — which the client only partially knows).
 */

const SEARCH_URL = `${CONFIG.BASE_URL}/api/v1/search/mine`;

/**
 * Run a global search over the caller's own data. Accepts an AbortSignal so a
 * superseded keystroke's request can be cancelled — without it, responses can land
 * out of order and an older, slower response overwrites a newer one (results
 * flickering back to a previous query).
 *
 * Aborts propagate as-is so callers can ignore them via `isAbortError` rather than
 * rendering a cancelled request as a failure.
 */
export const globalSearch = async (query, { types, limit, signal } = {}) => {
  const params = new URLSearchParams({ q: query });
  if (types?.length) params.set("types", Array.isArray(types) ? types.join(",") : types);
  if (limit) params.set("limit", String(limit));

  // Same auth convention as every other portal call in this app: the bearer token
  // is read from localStorage at call time (there is no shared fetch wrapper or
  // interceptor to hang it off).
  const token = localStorage.getItem("portalToken");
  const res = await apiFetch(`${SEARCH_URL}?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || "Search failed");
  }

  return {
    query: data.query ?? query,
    groups: Array.isArray(data.groups) ? data.groups : [],
    totalResults: data.totalResults ?? 0,
  };
};

/** The entity types the server can search — the server's whitelist, not a copy. */
export const fetchSearchTypes = async () => {
  const token = localStorage.getItem("portalToken");
  const res = await apiFetch(`${SEARCH_URL}/types`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return [];
  const data = await res.json().catch(() => ({}));
  return Array.isArray(data.types) ? data.types : [];
};

/**
 * Was this error just a cancelled request? The native fetch/AbortController path
 * surfaces `AbortError`, which means "superseded", not "failed" — callers must not
 * show an error state for it.
 */
export const isAbortError = (error) => error?.name === "AbortError";

export default globalSearch;
